import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEOHead } from '../components/SEO';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      <SEOHead
        title="Terms of Service | The Hotel Emerald"
        description="Terms and Conditions for The Hotel Emerald. Read our booking policies, cancellation terms, and guest responsibilities."
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-emerald-950 py-20">
        <div className="container-custom text-center text-white">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-serif text-4xl md:text-5xl mb-4"
          >
            Terms of Service
          </motion.h1>
          <p className="text-emerald-200">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">1. Reservation & Booking</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All reservations are subject to availability and confirmation.</li>
                <li>A valid credit card or advance payment may be required to secure your booking.</li>
                <li>Room rates are quoted in Indian Rupees (INR) and include applicable taxes unless otherwise stated.</li>
                <li>Special requests (early check-in, late check-out, specific room) are subject to availability and may incur additional charges.</li>
                <li>The hotel reserves the right to request a valid government-issued ID at check-in.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">2. Check-in & Check-out</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Check-in Time:</strong> 2:00 PM onwards</li>
                <li><strong>Check-out Time:</strong> 11:00 AM</li>
                <li>Early check-in and late check-out are subject to availability and may incur additional charges.</li>
                <li>Guests must be at least 18 years old to check in independently.</li>
                <li>Valid photo identification is required for all guests at check-in.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">3. Cancellation Policy</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Free Cancellation:</strong> Up to 48 hours before check-in date.</li>
                <li><strong>Late Cancellation:</strong> Cancellations within 48 hours may be charged one night's room rate.</li>
                <li><strong>No-Show:</strong> Full booking amount may be charged.</li>
                <li><strong>Peak Season/Festivals:</strong> Special cancellation terms may apply during Rath Yatra, Diwali, and New Year periods.</li>
                <li>Refunds will be processed within 7-14 business days to the original payment method.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">4. Payment Terms</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>We accept major credit cards, debit cards, UPI, and cash payments.</li>
                <li>Full payment is required at check-in unless pre-paid through our booking partners.</li>
                <li>A security deposit may be required for incidentals and room damages.</li>
                <li>All rates are subject to change without prior notice until booking is confirmed.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">5. Guest Conduct</h2>
              <p className="text-muted-foreground mb-4">Guests are expected to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Respect hotel property, staff, and other guests</li>
                <li>Maintain reasonable noise levels, especially during night hours (10 PM - 7 AM)</li>
                <li>Not engage in any illegal activities on the premises</li>
                <li>Report any damage to hotel property immediately</li>
                <li>Follow fire safety and emergency evacuation procedures</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                The hotel reserves the right to refuse service or evict guests who violate these terms without refund.
              </p>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">6. Smoking & Alcohol Policy</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All indoor areas are strictly non-smoking. Designated smoking areas are available.</li>
                <li>A cleaning fee of ₹5,000 will be charged for smoking in non-designated areas.</li>
                <li>Alcohol consumption is permitted only in designated areas and rooms (for guests 21+).</li>
                <li>Outside alcohol is not permitted in public areas of the hotel.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">7. Pet Policy</h2>
              <p className="text-muted-foreground mb-4">
                Currently, The Hotel Emerald does not allow pets on the premises, except for certified service animals. Please contact us in advance if you require accommodation for a service animal.
              </p>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">8. Liability</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>The hotel is not responsible for loss, theft, or damage to guest belongings. Safe deposit boxes are available at the reception.</li>
                <li>Guests are liable for any damage caused to hotel property during their stay.</li>
                <li>The hotel is not liable for injuries caused by guest negligence.</li>
                <li>Force majeure events (natural disasters, strikes, etc.) may result in booking modifications without liability.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">9. Website Usage</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All content on this website is owned by The Hotel Emerald and protected by copyright.</li>
                <li>Users may not reproduce, distribute, or commercially exploit website content without permission.</li>
                <li>We are not responsible for third-party website content linked from our site.</li>
                <li>Prices and availability shown online are subject to change until booking is confirmed.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">10. Dispute Resolution</h2>
              <p className="text-muted-foreground mb-4">
                Any disputes arising from these terms shall be governed by the laws of India and subject to the exclusive jurisdiction of courts in Koraput, Odisha.
              </p>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">11. Contact Information</h2>
              <p className="text-muted-foreground mb-4">
                For questions about these Terms of Service, please contact:
              </p>
              <div className="bg-muted p-6 rounded-lg">
                <p className="text-foreground font-medium">The Hotel Emerald</p>
                <p className="text-muted-foreground">NALCO Chowk, Semiliguda</p>
                <p className="text-muted-foreground">Koraput, Odisha 764036, India</p>
                <p className="text-muted-foreground">Email: thehotelemerald@gmail.com</p>
                <p className="text-muted-foreground">Phone: +91 6372 350 065</p>
              </div>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">12. Changes to Terms</h2>
              <p className="text-muted-foreground mb-4">
                We reserve the right to modify these terms at any time. Updated terms will be posted on this page. Your continued use of our services constitutes acceptance of any changes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
